import type { HitResultItem } from 'vue-instantsearch/vue3/es';
import type { LineItem } from '~~/types/commercetools';

import { TYPESENSE_HISTORY } from '~~/constants/localStorage';
import { extractGoogleAnalyticsUserIdFromCookie } from '~~/helpers/googleAnalytics';
import { i18nToTypesenseMainIndex } from '~~/helpers/locales/i18nCurrentDetails';

type TypesenseClickHistory = {
    [objectID: string]: { searchTerm: string, index: string }
}

type TypesenseQueries = {
    [searchTerm: string]: { objectIDs: string[], index: string }
}

export default function () {

  const { googleUserToken, typesenseUserToken } = useCookies();
  const i18n = useI18n();
  const { trackViewHitsEvent, trackClickHitEvent, trackConversionEvent } = useGtmEvents();
  const typesenseIndex = i18nToTypesenseMainIndex(i18n);
  const userToken = extractGoogleAnalyticsUserIdFromCookie(googleUserToken.value) || typesenseUserToken.value;

  function setTypesenseClickHistory(object: TypesenseClickHistory) {

    localStorage.setItem(TYPESENSE_HISTORY, JSON.stringify(object));

  }

  function getTypesenseClickHistory(): TypesenseClickHistory | null {

    const rawLocalStorage = localStorage.getItem(TYPESENSE_HISTORY);

    if (!rawLocalStorage) {

      return null;

    }

    return JSON.parse(rawLocalStorage);

  }

  function prepareTypesenseQueries(skus: string[]): TypesenseQueries {

    const typesenseClickHistory = getTypesenseClickHistory();

    if (!typesenseClickHistory) {

      return {};

    }

    const queries: TypesenseQueries = {};
    for (const sku of skus) {

      const clickHistory = typesenseClickHistory[sku];

      if (!clickHistory) {

        continue;

      }

      const { searchTerm, index } = clickHistory;
      const query = queries[searchTerm];

      if (query) {

        query.objectIDs.push(sku);

      } else {

        queries[searchTerm] = { objectIDs: [sku], index };

      }

      delete typesenseClickHistory[sku];

    }
    setTypesenseClickHistory(typesenseClickHistory);

    return queries;

  }

  function addToTypesenseClickHistory(
    { objectID, index, searchTerm }:
    { objectID: string, index: string, searchTerm: string },
  ) {

    let typesenseClickHistory = getTypesenseClickHistory();

    if (!typesenseClickHistory) {

      typesenseClickHistory = { [objectID]: { searchTerm, index } };

    } else {

      typesenseClickHistory[objectID] = { searchTerm, index };

    }
    setTypesenseClickHistory(typesenseClickHistory);

  }

  function getTypesenseQueries(products: LineItem[]): TypesenseQueries {

    const filteredSkus = products?.flatMap((product) => {

      const sku = product.variant?.sku;

      return sku ? [sku] : [];

    }) || [];
    const queries = prepareTypesenseQueries(filteredSkus);

    return queries;

  }

  function callViewHitsEvent(hits: HitResultItem[]) {

    const filteredObjectIDs = hits?.flatMap((hit) => {

      const objectID = hit.objectID;

      return objectID ? [objectID] : [];

    }) || [];
    trackViewHitsEvent({ userToken, index: typesenseIndex, objectIDs: filteredObjectIDs });

  }

  function callClickHitEvent(item: HitResultItem, searchTerm: string) {

    const { __position: position, objectID } = item;

    if (!objectID) {

      return;

    }

    const eventParameters = {
      userToken,
      index: typesenseIndex,
      objectIDs: [objectID],
      searchTerm,
      positions: position ? [position] : [],
    };

    addToTypesenseClickHistory({ objectID, index: typesenseIndex, searchTerm });
    trackClickHitEvent(eventParameters);

  }

  function callConversionEvent(products: LineItem[]) {

    const queries = getTypesenseQueries(products);

    for (const [searchTerm, query] of Object.entries(queries)) {

      if (query) {

        const { objectIDs, index } = query;
        trackConversionEvent({ userToken, index, objectIDs, searchTerm });

      }

    }

  }

  return {
    callViewHitsEvent,
    callClickHitEvent,
    callConversionEvent,
  };

}
